import { useEffect, useState, useRef, useCallback } from "react";

/**
 * Tracks a form's dirty state relative to a snapshot. Warns the user on
 * beforeunload if they try to close the tab with unsaved changes.
 *
 * Usage:
 *   const { isDirty, snapshot, markSaved, checkDirty } = useDirtyState(currentValues);
 *   // whenever `currentValues` changes, isDirty toggles automatically
 *   // call markSaved(currentValues) after a successful save
 */
export default function useDirtyState(current, { compare } = {}) {
    const [snapshot, setSnapshot] = useState(() => _clone(current));
    const [isDirty, setIsDirty] = useState(false);

    const equal = useCallback((a, b) => {
        if (compare) return compare(a, b);
        return JSON.stringify(a) === JSON.stringify(b);
    }, [compare]);

    useEffect(() => {
        setIsDirty(!equal(current, snapshot));
    }, [current, snapshot, equal]);

    // beforeunload guard
    useEffect(() => {
        if (!isDirty) return;
        const handler = (e) => {
            e.preventDefault();
            e.returnValue = "You have unsaved changes. Leave anyway?";
            return e.returnValue;
        };
        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, [isDirty]);

    const markSaved = useCallback((next) => {
        setSnapshot(_clone(next !== undefined ? next : current));
        setIsDirty(false);
    }, [current]);

    const checkDirty = useCallback((confirmText = "You have unsaved changes. Discard them?") => {
        if (!isDirty) return true;
        return window.confirm(confirmText);
    }, [isDirty]);

    return { isDirty, snapshot, markSaved, checkDirty };
}

function _clone(v) {
    try { return v === undefined ? undefined : JSON.parse(JSON.stringify(v)); }
    catch { return v; }
}
